import classNames from 'classnames';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { IResortShortReport } from 'api/region/types';
import TitleCell from 'components/common/Grid/Cells/TitleCell';
import SnowfallCell from 'components/common/Grid/Cells/SnowfallCell';
import BaseDepthCell from 'components/common/Grid/Cells/BaseDepthCell';
import OpenTrailsCell from 'components/common/Grid/Cells/OpenTrailsCell';
import OpenLiftsCell from 'components/common/Grid/Cells/OpenLiftsCell';
import { OpenStatus } from 'components/blocks/OpenStatus';
import { DisplayType } from 'components/region/WeatherRow/types';
import { Routes } from 'routes';
import { OpenFlag, ServiseOpenFlag } from 'api/resort/types';
import gridCellsStyles from 'components/common/Grid/Cells/styles.module.scss';
import gridHeaderStyles from 'components/common/Grid/styles.module.scss';
import { formatDateFromStr, SHORT_DATE_FORMAT } from 'util/formatDate';
import { IGridColumn } from 'components/common/Grid/types';

import Days3ForecastCell from 'components/common/Grid/Cells/3DaysForecastCell';
import styles from './styles.module.scss';

export const createRegionWeatherGridColumns = (openFlag?: DisplayType, isSortableFlag?: boolean, displayRegion?: boolean,
  useSkiResortLink: boolean = false) => {
  const isSortable = isSortableFlag ?? true;
  const is72hoursReport = openFlag === 'top-72';
  const { t } = useOTSTranslation('common');
  const snowfallColumnName = is72hoursReport ? t('snowWeather.home.72hourSnowfall') : t('resort.skiReport.72hSnowfall');
  const snowfallColumnNameAbb = t('region.skiReport.snowfall-72h-abb');
  const showStatus = openFlag && openFlag !== ServiseOpenFlag.OpenResorts && openFlag !== ServiseOpenFlag.top72;
  const requiredTitleCell = {
    header: {
      cellRender: () => (
        <div>
          {showStatus && <OpenStatus flag={openFlag} />}
          <span>{t('region.liftTickets.resort')}</span>
        </div>
      ),
      className: classNames(
        {
          h3: showStatus,
          [gridCellsStyles.titleCell]: showStatus,
          [gridCellsStyles.titleHeaderCell]: showStatus,
          [styles.resortNameHeader]: showStatus,
          [gridHeaderStyles.firstColumn]: !showStatus,
        },
      ),
      isSortable,
      columnName: t('region.liftTickets.resort'),
    },
    dataKey: 'title',
    cellRender: (resort: IResortShortReport) => (
      <TitleCell
        title={displayRegion ? `${resort.title}, ${resort.region_en}` : `${resort.title}`}
        href={useSkiResortLink ? Routes.resortTripPlanningOverview(resort) : Routes.resortSnowReport(resort)}
        updatedDate={resort.status.openFlag !== OpenFlag.NoReportAvailable ? resort.updatedAt : undefined}
      />
    ),
  };

  const columns: IGridColumn[] = [requiredTitleCell];

  if (openFlag === OpenFlag.Closed) {
    columns.push(
      {
        header: {
          columnName: t('nav.tab.OpenDates'),
          isSortable,
          className: styles.togglingCellTitle,
          cellRender: () => (
            <span>
              <span>{t('nav.tab.OpenDates')}</span>
              <span>{t('region.skiReport.opening')}</span>
            </span>
          ),
        },
        dataKey: 'opening-date',
        cellRender: (row: IResortShortReport) => (
          <span className={classNames('h4', gridCellsStyles.h4)}>
            {formatDateFromStr(row.status.openingDate, SHORT_DATE_FORMAT)}
            <div className={classNames(gridCellsStyles.small, gridCellsStyles.hiddenCell)}>-</div>
          </span>
        ),
      },
    );
  }

  if (openFlag !== OpenFlag.Closed) {
    columns.push(
      {
        header: {
          columnName: snowfallColumnName,
          isSortable,
          className: styles.togglingCellTitle,
          cellRender: () => (
            <span>
              <span>{snowfallColumnName}</span>
              <span>{snowfallColumnNameAbb}</span>
            </span>
          ),
        },
        dataKey: 'last-snow',
        cellRender: (row: IResortShortReport) => (
          <SnowfallCell
            openFlag={row.status.openFlag}
            currentWeather={row.snow}
            noValue={t('common.dash')}
            is72hoursReport
            updatedAt={row.forecast?.[0]?.date}
          />
        ),
      },
      {
        header: {
          columnName: '3dayForecast',
          isSortable,
          className: styles.togglingCellTitle,
          cellRender: () => (
            <span>
              <span>{t('region.skiReport.snowForecast')}</span>
              <span>{t('region.skiReport.snowForecast')}</span>
            </span>
          ),
        },
        dataKey: '3day-snow-forecast',
        cellRender: (row: IResortShortReport) => (
          <Days3ForecastCell
            openFlag={row.status.openFlag}
            row={row}
            noValue={t('common.dash')}
            updatedAt={row.forecast?.[0]?.date}
          />
        ),
      },
      {
        header: {
          columnName: t('region.skiReport.baseDepth'),
          isSortable,
          className: styles.togglingCellTitle,
          cellRender: () => (
            <span>
              <span>{t('region.skiReport.baseDepth')}</span>
              <span>{t('region.skiReport.base-abb')}</span>
            </span>
          ),
        },
        dataKey: 'snow-base',
        cellRender: (row: IResortShortReport) => (
          <BaseDepthCell
            openFlag={row.status.openFlag}
            snow={row.snow}
            surfaceType={row.surfaceType}
            noValue={t('common.dash')}
          />
        ),
      },
      {
        header: {
          columnName: t('region.skiReport.openTrails'),
          isSortable,
          className: styles.togglingCellTitle,
          cellRender: () => (
            <span>
              <span>{t('region.skiReport.openTrails')}</span>
              <span>{t('region.skiReport.trails-abb')}</span>
            </span>
          ),
        },
        dataKey: 'runs-open',
        cellRender: (row: IResortShortReport) => (
          <OpenTrailsCell
            openFlag={row.status.openFlag}
            kilometres={row.km}
            runs={row.runs}
            noValue={t('common.dash')}
          />
        ),
      },
      {
        header: {
          columnName: t('region.skiReport.openLifts'),
          isSortable,
          className: styles.togglingCellTitle,
        },
        dataKey: 'lifts-open',
        cellRender: (row: IResortShortReport) => (
          <OpenLiftsCell
            openFlag={row.status.openFlag}
            lifts={row.lifts}
            noValue={t('common.dash')}
          />
        ),
      },
    );
  }

  return columns;
};
